class DownloadQueue {
  constructor() {
    this.queue = [];
    this.listeners = [];
    this.maxConcurrency = 3; // Limit to 3 concurrent downloads to prevent browser/network throttling
    this.activeCount = 0; // Number of items currently processing
    this.lastTriggerTime = 0; // Tracks the last time a download was triggered
  }

  // Subscribe to queue changes
  subscribe(callback) {
    this.listeners.push(callback);
    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter(cb => cb !== callback);
    };
  }

  // Notify all listeners of queue changes
  notify() {
    const state = {
      queue: this.queue.map(item => ({
        id: item.id,
        versionType: item.metadata.versionType,
        trackTitle: item.metadata.trackTitle,
        editTitle: item.metadata.editTitle,
        status: item.status,
        versionId: item.metadata.versionId,
      })),
      isProcessing: this.activeCount > 0,
      currentDownload: this.queue.find(item => item.status === 'downloading'),
    };
    
    this.listeners.forEach(callback => callback(state));
  }

  // Add single download to queue
  add(id, downloadFn, metadata) {
    const downloadedCount = metadata.downloadedCount || 0;
    
    // Count how many instances of this version are already in the queue (pending or downloading)
    const inQueueCount = this.queue.filter(
      item => item.metadata.versionId === metadata.versionId &&
              (item.status === 'pending' || item.status === 'downloading')
    ).length;

    if (downloadedCount + inQueueCount >= 2) {
      console.log(`[Queue] Version ${metadata.versionId} has reached download limit (downloaded: ${downloadedCount}, in queue: ${inQueueCount}). Ignoring duplicate click.`);
      return null;
    }

    // Cooldown check: prevent duplicate clicks for the same version within 2000ms
    const lastAddedOfSameVersion = this.queue
      .filter(item => item.metadata.versionId === metadata.versionId)
      .sort((a, b) => b.addedAt - a.addedAt)[0];

    if (lastAddedOfSameVersion && (Date.now() - lastAddedOfSameVersion.addedAt < 2000)) {
      console.log(`[Queue] Version ${metadata.versionId} was added recently within 2000ms. Ignoring duplicate rapid click.`);
      return null;
    }

    const queueItem = {
      id,
      fn: downloadFn,
      metadata,
      status: 'pending',
      addedAt: Date.now(),
    };

    this.queue.push(queueItem);
    console.log(`[Queue] Added item: ${metadata.trackTitle} (${metadata.versionType}). Total queue length: ${this.queue.length}`);
    this.notify();

    // Start processing
    this.process();

    return id;
  }

  // Add multiple downloads at once
  addBatch(downloads) {
    const ids = [];
    
    downloads.forEach(({ id, downloadFn, metadata }) => {
      const downloadedCount = metadata.downloadedCount || 0;
      
      // Count how many instances of this version are already in the queue (pending or downloading)
      const inQueueCount = this.queue.filter(
        item => item.metadata.versionId === metadata.versionId &&
                (item.status === 'pending' || item.status === 'downloading')
      ).length;

      if (downloadedCount + inQueueCount >= 2) return;

      const queueItem = {
        id,
        fn: downloadFn,
        metadata,
        status: 'pending',
        addedAt: Date.now(),
      };
      
      this.queue.push(queueItem);
      ids.push(id);
    });

    console.log(`[Queue] Added batch of ${ids.length} items. Total queue length: ${this.queue.length}`);
    this.notify();
    this.process();
    return ids;
  }

  process() {
    console.log(`[Queue] Checking queue processing. Active downloads: ${this.activeCount}/${this.maxConcurrency}. Pending items: ${this.queue.filter(i => i.status === 'pending').length}`);
    
    // Flat event loop: Trigger new download tasks while concurrency slots are available
    while (this.activeCount < this.maxConcurrency) {
      const nextItem = this.queue.find(item => item.status === 'pending');
      if (!nextItem) {
        break;
      }

      this.runItem(nextItem);
    }
  }

  async runItem(item) {
    item.status = 'downloading';
    this.activeCount++;
    this.notify();

    try {
      console.log(`[Queue] Starting download run: ${item.metadata.trackTitle} (${item.metadata.versionType})`);

      // Enforce sequential trigger spacing delay (minimum 300ms spacing)
      const now = Date.now();
      const elapsed = now - this.lastTriggerTime;
      const minSpacing = 300;
      if (elapsed < minSpacing) {
        const delay = minSpacing - elapsed;
        this.lastTriggerTime = now + delay; // Reserve slot in the future to keep multiple concurrent triggers spaced
        console.log(`[Queue] Spacing download trigger for "${item.metadata.trackTitle}" by ${delay}ms`);
        await new Promise(resolve => setTimeout(resolve, delay));
      } else {
        this.lastTriggerTime = now;
      }

      // Execute the download logic (generate signed URL, check eligibility, trigger iframe)
      await item.fn();
      
      item.status = 'completed';
      
      // Remove completed item from queue immediately
      const index = this.queue.findIndex(qItem => qItem.id === item.id);
      if (index !== -1) {
        this.queue.splice(index, 1);
      }
      console.log(`[Queue] Completed download task: ${item.metadata.trackTitle} (${item.metadata.versionType})`);
    } catch (error) {
      console.error(`[Queue] Failed download task: ${item.metadata.trackTitle} (${item.metadata.versionType})`, error);
      item.status = 'failed';
      this.notify();
      
      // Remove failed item after 3 seconds
      setTimeout(() => {
        const index = this.queue.findIndex(qItem => qItem.id === item.id);
        if (index !== -1) {
          this.queue.splice(index, 1);
          this.notify();
        }
      }, 3000);
    } finally {
      this.activeCount--;
      console.log(`[Queue] Finished download task lifecycle. Active downloads: ${this.activeCount}/${this.maxConcurrency}`);
      this.notify();
      
      // Trigger process to check for next pending items
      this.process();
    }
  }

  // Remove specific download from queue
  remove(id) {
    const index = this.queue.findIndex(item => item.id === id);
    if (index !== -1) {
      const item = this.queue[index];
      if (item.status === 'downloading') {
        this.activeCount--;
      }
      this.queue.splice(index, 1);
      console.log(`[Queue] Removed item by ID: ${id}. Active downloads: ${this.activeCount}`);
      this.notify();
      this.process();
    }
  }

  // Clear all pending (not downloading) items
  clearPending() {
    const originalLength = this.queue.length;
    this.queue = this.queue.filter(item => item.status === 'downloading');
    console.log(`[Queue] Cleared pending downloads. Removed ${originalLength - this.queue.length} items.`);
    this.notify();
  }

  // Get current state
  getState() {
    return {
      queue: [...this.queue],
      isProcessing: this.activeCount > 0,
      queueLength: this.queue.length,
    };
  }

  getStats() {
    return {
      running: this.activeCount,
      pending: this.queue.filter(item => item.status === 'pending').length,
    };
  }
}

export const downloadQueue = new DownloadQueue();
